import { catchError, filter, forkJoin, from, map, mergeMap, Observable, switchMap } from 'rxjs';
import { ofType } from 'redux-observable';
import { providerSlice, regenerateNoteTemplateRequested } from './';
import { authSlice } from '../auth';
import { Auth } from '@ax/auth';
import { appSlice } from '../app';
import {
  getProviderIntegrationInfo,
  getProviderProductOffering,
  getProviderSettings,
} from '@ax/service';
import { getProviderNoteTemplates } from '@ax/service';
import { client } from '@ax/graphql';
import { of } from 'rxjs';
import { SAVE_NOTE } from '@ax/graphql';
import { reprocessNote } from '@ax/service';
import { EMPTY } from 'rxjs';
import { noteSlice } from '../note';

export const onAppInitOrUserSignedInInitializeProviderState = (action$: Observable<any>) =>
  action$.pipe(
    ofType(authSlice.actions.userSignedIn.type, appSlice.actions.appInitialized.type),
    filter(() => Auth.isLoggedIn()),
    mergeMap(() => {
      return [providerSlice.actions.initializeProviderStateRequested()];
    }),
  );

export const onInitializeProviderStateRequested = (action$: Observable<any>) =>
  action$.pipe(
    ofType(providerSlice.actions.initializeProviderStateRequested.type),
    switchMap(() => {
      try {
        return forkJoin({
          providerSettings: from(getProviderSettings()).pipe(
            map((response) => ({ data: response, error: null, errorMessage: '' })),
            catchError((error) => [
              {
                error,
                errorMessage:
                  'An error occurred while retrieving the settings for the current user',
              },
            ]),
          ),
          providerProductOffering: from(getProviderProductOffering()).pipe(
            map((response) => ({ data: response, error: null, errorMessage: '' })),
            catchError((error) => [
              {
                error,
                errorMessage:
                  'An error occurred while retrieving the product offering for the current user',
              },
            ]),
          ),
          providerIntegrationInfo: from(getProviderIntegrationInfo(Auth.currentUser().uid)).pipe(
            map((response) => ({ data: response, error: null, errorMessage: '' })),
            catchError((error) => [
              {
                error,
                errorMessage:
                  'An error occurred while retrieving the integration info for the current user',
              },
            ]),
          ),
        }).pipe(
          map((responses) => {
            const errors = Object.values(responses).filter((response) => response.error);
            if (errors.length) {
              return providerSlice.actions.initializeProviderStateFailed({
                errors: errors.map((error) => ({
                  error: error.error,
                  message: error.errorMessage,
                })),
              });
            }
            return providerSlice.actions.initializeProviderStateSucceeded(responses);
          }),
          catchError((error) => {
            return [
              providerSlice.actions.initializeProviderStateFailed({
                errors: [
                  {
                    error,
                    message: 'An unknown error occurred while initializing the provider state',
                  },
                ],
              }),
            ];
          }),
        );
      } catch (error) {
        return [
          providerSlice.actions.initializeProviderStateFailed({
            errors: [
              { error, message: 'An unknown error occurred while initializing the provider state' },
            ],
          }),
        ];
      }
    }),
  );

export const onNoteTemplatesRequested = (action$: Observable<any>) =>
  action$.pipe(
    ofType(providerSlice.actions.initializeProviderStateSucceeded.type),
    switchMap((providerDetails) => {
      return from(
        getProviderNoteTemplates(providerDetails.payload.providerSettings.data.email),
      ).pipe(
        map((response) => {
          return providerSlice.actions.loadNoteTemplatesSucceeded({ templates: response });
        }),
        catchError((error) => {
          return [
            providerSlice.actions.loadNoteTemplatesFailed({
              error: error.message,
              message: 'An error occurred while retrieving the note templates',
            }),
          ];
        }),
      );
    }),
  );

export const onUpdateNoteTemplate = (action$: Observable<any>) =>
  action$.pipe(
    ofType(providerSlice.actions.updateNoteTemplateRequested.type),
    switchMap((action) => {
      const { noteId, templateId, regenerateNote } = action.payload;
      const mutation = {
        mutation: SAVE_NOTE,
        variables: {
          input: {
            noteId: noteId,
            templateIdSelected: templateId.toString(),
          },
        },
      };

      return from(client.mutate(mutation)).pipe(
        switchMap(() => {
          if (regenerateNote) {
            return from([
              noteSlice.actions.updateNoteTemplateSucceeded(action.payload),
              regenerateNoteTemplateRequested({ noteId }),
            ]);
          }
          return of(noteSlice.actions.updateNoteTemplateSucceeded(action.payload));
        }),
        catchError((error) => {
          return [
            providerSlice.actions.updateNoteTemplateFailed({
              error,
              message: 'An error occurred while updating the note template',
            }),
          ];
        }),
      );
    }),
  );

export const onReprocessNoteTemplate = (action$: Observable<any>) =>
  action$.pipe(
    ofType(providerSlice.actions.regenerateNoteTemplateRequested.type),
    switchMap((action) => {
      return from(reprocessNote(action.payload.noteId)).pipe(
        switchMap(() => {
          return EMPTY;
        }),
        catchError((error) => {
          return [
            providerSlice.actions.regenerateNoteTemplateFailed({
              error,
              message: 'An error occurred while regenerating the note',
            }),
          ];
        }),
      );
    }),
  );

export default [
  onAppInitOrUserSignedInInitializeProviderState,
  onInitializeProviderStateRequested,
  onNoteTemplatesRequested,
  onReprocessNoteTemplate,
  onUpdateNoteTemplate,
];
